/**
 * Cart drawer — common Design tokens (Essential Cart–style).
 * One compact Design panel; tokens cascade into existing section CSS.
 */

/** True for legacy footer titles that mean "Total MRP" in the bill sheet. */
export function isLegacyEstimatedTotalLabel(raw) {
  const label = String(raw || "").trim();
  return !label || /^subtotal$/i.test(label) || /^estimated total$/i.test(label);
}

/** Prefer merchant Total MRP copy; remap legacy Subtotal / Estimated Total. */
export function resolveTotalMrpLabelText(...candidates) {
  for (const raw of candidates) {
    const label = String(raw ?? "").trim();
    if (!label || isLegacyEstimatedTotalLabel(label)) continue;
    return label;
  }
  return "Total MRP";
}

export const CART_DRAWER_DESIGN_DEFAULTS = {
  fontFamily: "theme-default",
  /** Buttons, badges, quantity, upsell Add (not the outer panel). */
  cornerRadius: 12,
  /**
   * Outer cart panel corners. When unset, falls back to cornerRadius so
   * older carts that only saved one radius stay consistent.
   */
  shellCornerRadius: null,
  backgroundColor: "#ffffff",
  borderColor: "#e3e3e3",
  primaryText: "#303030",
  secondaryText: "#616161",
  priceColor: "#000000",
  /** Desktop floating inset around the panel. Mobile stays edge-to-edge. */
  sideMarginEnabled: true,
  sideMarginPixels: 10,
  customCss: "",
};

/** Clamp a corner-radius token to 0–48px. */
export function clampCartDrawerCornerRadius(value, fallback = 12) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(48, Math.round(n)));
}

/**
 * Pre-Design-tab look for carts that never saved layout.design.
 * Keeps existing storefronts stable when Design tokens are absent.
 */
export const CART_DRAWER_LEGACY_SHELL = {
  fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
  cornerRadius: 20,
  backgroundColor: "#ffffff",
  borderColor: "rgba(15, 23, 42, 0.08)",
  primaryText: "#0f172a",
  secondaryText: "#757575",
  priceColor: "#0f172a",
  sideMarginEnabled: true,
  sideMarginPixels: 10,
};

/** True when the merchant (or brand kit) has written compact Design tokens. */
export function hasCartDrawerDesignTokens(layout = {}) {
  const design = layout?.design;
  if (!design || typeof design !== "object") return false;
  return Object.keys(design).some((key) => {
    const value = design[key];
    if (value == null) return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  });
}

/**
 * Strip dangerous CSS constructs before injecting merchant custom CSS.
 * Allows normal selectors/properties; blocks imports, expressions, and JS URLs.
 * Scoped to `.weu-custom-cart-drawer` so rules cannot restyle the host page.
 */
export function sanitizeMerchantCustomCss(raw) {
  let css = String(raw || "");
  if (!css.trim()) return "";
  // Cap size to avoid huge style payloads.
  if (css.length > 20000) css = css.slice(0, 20000);

  css = css
    .replace(/@import\b[^;]*;?/gi, "")
    .replace(/@charset\b[^;]*;?/gi, "")
    .replace(/@namespace\b[^;]*;?/gi, "")
    .replace(/expression\s*\((?:[^()]|\([^()]*\))*\)/gi, "none")
    .replace(/javascript\s*:/gi, "blocked:")
    .replace(/vbscript\s*:/gi, "blocked:")
    .replace(/-moz-binding\s*:/gi, "blocked:")
    .replace(/behavior\s*:/gi, "blocked:");

  // Drop html/body/document-level escapes outside the drawer host.
  css = css.replace(
    /(^|})\s*(html|body|:root)\b[^{]*\{[^}]*\}/gi,
    "$1"
  );

  css = css.trim();
  if (!css) return "";

  // Scope to the cart drawer host (safe for light DOM preview + shadow storefront).
  return `@scope (.weu-custom-cart-drawer) {\n${css}\n}`;
}

export function readCartDrawerDesignTokens(layout = {}, popupSettings = {}) {
  const design = layout?.design && typeof layout.design === "object"
    ? layout.design
    : {};
  const shell = popupSettings?.customCSS?.desktop || {};
  const explicit = hasCartDrawerDesignTokens(layout);
  // Editor / brand kit path: compact Design defaults.
  // Storefront carts with no layout.design keep the legacy shell look.
  const base = explicit
    ? CART_DRAWER_DESIGN_DEFAULTS
    : CART_DRAWER_LEGACY_SHELL;
  const corner = Number(
    design.cornerRadius ??
      (shell.borderRadius != null &&
      String(shell.borderRadius).trim() !== "" &&
      String(shell.borderRadius) !== "0px"
        ? parseFloat(String(shell.borderRadius))
        : base.cornerRadius)
  );
  const cornerRadius = Number.isFinite(corner)
    ? clampCartDrawerCornerRadius(corner, base.cornerRadius)
    : base.cornerRadius;
  // Prefer explicit shell token, then popupSettings shell CSS, then button radius.
  const shellCornerRaw =
    design.shellCornerRadius != null && design.shellCornerRadius !== ""
      ? Number(design.shellCornerRadius)
      : shell.borderRadius != null && String(shell.borderRadius).trim() !== ""
        ? parseFloat(String(shell.borderRadius))
        : cornerRadius;
  const shellCornerRadius = clampCartDrawerCornerRadius(
    shellCornerRaw,
    cornerRadius
  );

  return {
    hasExplicitDesign: explicit,
    fontFamily:
      design.fontFamily ||
      shell.fontFamily ||
      base.fontFamily,
    cornerRadius,
    shellCornerRadius,
    backgroundColor:
      design.backgroundColor ||
      shell.backgroundColor ||
      base.backgroundColor,
    borderColor:
      design.borderColor ||
      shell.borderColor ||
      base.borderColor,
    primaryText: design.primaryText || base.primaryText,
    secondaryText: design.secondaryText || base.secondaryText,
    priceColor: design.priceColor || base.priceColor,
    sideMarginEnabled:
      design.sideMarginEnabled !== false &&
      design.sideMarginEnabled !== 0,
    sideMarginPixels: (() => {
      const n = Number(
        design.sideMarginPixels ?? base.sideMarginPixels
      );
      return Number.isFinite(n) ? Math.max(0, Math.min(48, Math.round(n))) : 10;
    })(),
    customCss:
      typeof design.customCss === "string"
        ? design.customCss
        : CART_DRAWER_DESIGN_DEFAULTS.customCss,
  };
}

export function writeCartDrawerDesignTokens(layout, tokens = {}) {
  if (!layout || typeof layout !== "object") return layout;
  layout.design = {
    ...(layout.design || {}),
    ...tokens,
  };
  return layout;
}

/** Desktop float inset in px (0 when disabled or mobile). */
export function resolveCartDrawerSideMarginPx(design, isMobile) {
  if (isMobile) return 0;
  const tokens = { ...CART_DRAWER_DESIGN_DEFAULTS, ...(design || {}) };
  if (tokens.sideMarginEnabled === false || tokens.sideMarginEnabled === 0) {
    return 0;
  }
  const n = Number(tokens.sideMarginPixels);
  return Number.isFinite(n) ? Math.max(0, Math.min(48, Math.round(n))) : 10;
}
